import { Experiment, ExperimentClient, ExperimentUser } from '@amplitude/experiment-js-client';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import envService from './env.service';

// Feature flags that have a boolean value
export enum FeatureFlags {
  Test = 'test',
  IsSkipAccountCreationEnabled = 'skip-email-capture-pre-checkout-grow-131_v2',
  IsRiskTrendTabEnabled = 'risk-trend-tab-enabled',
  IsRiskAssessmentUpgradeEnabled = 'risk-assessment-upgrade',
  IsSinglePaymentEnabled = 'single-payment-enabled',
  IsLlmDoctorsNoteEnabled = 'llm-doctors-note-enabled',
  IsPaymentPageV2Enabled = 'payment-page-v2-enabled',
}

// Feature flags that have multiple variants as a string
export enum VariantFlags {
  PPRAVersion = 'ppra-version',
  IsPpraMembershipInterstitialEnabled = 'ppra-membership-interstitial-enabled',
  RemoveNameFromEmailCapture = 'remove-name-from-email-capture-grow-671',
  ShowHeroVideoInterstitial = 'show-hero-video-interstitial-grow-670',
  QuizletVersion = 'grow-720-quizlet-version',
  PaymentPageFeaturesPosition = 'payment-page-features-position', // 'above' | 'below'
  PriceExperiment = 'price-experiment', // 'control' | 'experiment'
  SignUpExperiment = 'sign-up-experiment', // 'control' (pricing page) | 'quizlet' (quizletv2)
}

const defaultFeatureFlags: Record<VariantFlags, string> = {
  [VariantFlags.PPRAVersion]: 'email_first',
  [VariantFlags.IsPpraMembershipInterstitialEnabled]: 'control',
  [VariantFlags.RemoveNameFromEmailCapture]: 'control',
  [VariantFlags.ShowHeroVideoInterstitial]: 'control',
  [VariantFlags.QuizletVersion]: 'v6',
  [VariantFlags.PaymentPageFeaturesPosition]: 'below', // default: features below payment form
  [VariantFlags.PriceExperiment]: 'control', // default: control pricing ($119)
  [VariantFlags.SignUpExperiment]: 'control', // default: existing pricing page
};

export interface FeatureFlagContextValue {
  isEnabled: (name: FeatureFlags) => boolean;
  getVariant: (name: VariantFlags) => string;
  isLoading: boolean;
  setUser: (user?: ExperimentUser) => Promise<void>;
  instance?: ExperimentClient;
}

export const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export const FeatureFlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const [instance, setInstance] = useState<ExperimentClient>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeFeatureFlags = async () => {
      try {
        setIsLoading(true);

        // Initialize Experiment client with Amplitude Analytics integration
        // This ensures the device ID is properly shared between Analytics and Experiment
        const experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
          envService.getEnv().VITE_AMPLITUDE_API_KEY
        );

        await experimentInstance.fetch();

        setInstance(experimentInstance);

      } catch (error) {
        console.error('Failed to initialize feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFeatureFlags();
  }, []);

  const isEnabled = (name: FeatureFlags): boolean => {
    // Check localStorage for local dev overrides (e.g., localStorage.setItem('ff_payment-page-v2-enabled', 'true'))
    const localOverride = localStorage.getItem(`ff_${name}`);
    if (localOverride === 'true') return true;
    if (localOverride === 'false') return false;

    if (!instance || isLoading) return false;
    const variant = instance.variant(name);
    return variant.value === 'on';
  };

  const getVariant = (name: VariantFlags): string => {
    if (!instance || isLoading) return defaultFeatureFlags[name];
    const variant = instance.variant(name);
    return variant.value ?? defaultFeatureFlags[name];
  };

  const setUser = async (user?: ExperimentUser): Promise<void> => {
    if (instance && user) {
      // When using initializeWithAmplitudeAnalytics, the device ID is automatically
      // synchronized between Analytics and Experiment SDKs
      instance.setUser(user);
      await instance.fetch(user);
    }
  };

  const contextValue: FeatureFlagContextValue = {
    isEnabled,
    getVariant,
    isLoading,
    setUser,
    instance,
  };

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

/**
 * Hook for the LLM-driven doctors note feature with environment-aware defaults
 * and mutual exclusivity with the legacy download report feature.
 *
 * Default behavior:
 * - DEV/LOCAL: LLM doctors note enabled, legacy download report disabled
 * - PROD: LLM doctors note disabled, legacy download report enabled
 *
 * This can be overridden by Amplitude feature flags when configured.
 *
 * @returns Object with both feature states ensuring mutual exclusivity
 */
export const useDoctorsNoteFeatures = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useDoctorsNoteFeatures must be used within a FeatureFlagProvider');
  }

  const { instance, isLoading } = context;
  const deploymentTarget = envService.getEnv().VITE_DEPLOYMENT_TARGET;

  // Default: LLM note enabled in dev/local, disabled in prod
  const defaultLlmEnabled = deploymentTarget !== 'PROD';

  // Determine if LLM doctors note should be enabled
  let isLlmDoctorsNoteEnabled = defaultLlmEnabled;

  // After Amplitude loads, check if it has an explicit value set
  if (!isLoading && instance) {
    const variant = instance.variant(FeatureFlags.IsLlmDoctorsNoteEnabled);

    // Only override environment default if Amplitude has explicitly configured this flag
    if (variant.value === 'on') {
      isLlmDoctorsNoteEnabled = true;
    } else if (variant.value === 'off') {
      isLlmDoctorsNoteEnabled = false;
    }
    // If variant.value is undefined/null, keep the environment-based default
  }

  // Ensure mutual exclusivity: if LLM note is enabled, legacy download is disabled
  const isLegacyDownloadEnabled = !isLlmDoctorsNoteEnabled;

  return {
    isLlmDoctorsNoteEnabled,
    isLegacyDownloadEnabled,
    isLoading,
  };
};

export default FeatureFlagProvider;
